import React, { createContext, useContext, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'raptor_sidebar_collapsed';

const CollapsedCtx = createContext<boolean>(false);
export const useSidebarCollapsed = (): boolean => useContext(CollapsedCtx);

// Shared collapse state, persisted so the preference sticks across pages/roles.
export function useSidebarState(): { collapsed: boolean; toggle: () => void } {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const toggle = (): void =>
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  return { collapsed, toggle };
}

export const SideNav: React.FC<{
  collapsed: boolean;
  toggle: () => void;
  className?: string;
  toggleClassName?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ collapsed, toggle, className = '', toggleClassName = '', style, children }) => (
  <CollapsedCtx.Provider value={collapsed}>
    <aside
      data-testid="app-sidebar"
      data-collapsed={collapsed}
      style={style}
      className={`relative ${collapsed ? 'w-[76px]' : 'w-64'} transition-all duration-300 ease-in-out flex flex-col flex-shrink-0 ${className}`}
    >
      {children}
      <button
        type="button"
        onClick={toggle}
        data-testid="sidebar-toggle"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className={`absolute top-1/2 -right-3 -translate-y-1/2 z-20 h-6 w-6 rounded-full shadow-md flex items-center justify-center transition-transform hover:scale-110 ${toggleClassName}`}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
    </aside>
  </CollapsedCtx.Provider>
);

export const NavItem: React.FC<{
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  activeClass: string;
  idleClass: string;
}> = ({ to, icon, label, end, activeClass, idleClass }) => {
  const collapsed = useSidebarCollapsed();
  return (
    <NavLink
      to={to}
      end={end}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `flex items-center ${collapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2 rounded-md text-sm font-body transition-colors ${
          isActive ? activeClass : idleClass
        }`
      }
    >
      <span className="opacity-80 flex-shrink-0">{icon}</span>
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
};
